import React, { useState, useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import {
	DropdownItem,
	DropdownItemGroup,
	default as AkDropDownMenu,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import EditIcon from '@atlaskit/icon/core/edit';
import CopyIcon from '@atlaskit/icon/core/copy';
import DeleteIcon from '@atlaskit/icon/core/delete';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import AkTooltip from '@atlaskit/tooltip/Tooltip';

import { useSnippetActionHandlers } from '../hooks/useSnippetActionHandlers.ts';
import type { Snippet } from '../snippetTypes.ts';

const styles = cssMap({
	actionsContainer: {
		flexShrink: 0,
		opacity: 0,
		transition: 'opacity 0.15s ease-in-out',

		'&:focus-within': {
			opacity: 1,
		},
	},
	actionsContainerVisible: {
		opacity: 1,
	},
});

const messages = defineMessages({
	moreActions: {
		id: 'snippets.panel.item.more.actions',
		defaultMessage: 'Block Templates actions',
		description: 'Tooltip for block templates actions ellipsis menu',
	},
	edit: {
		id: 'snippets.panel.item.edit',
		defaultMessage: 'Edit',
		description: 'Edit block template menu item',
	},
	duplicate: {
		id: 'snippets.panel.item.duplicate',
		defaultMessage: 'Duplicate',
		description: 'Duplicate block template menu item',
	},
	delete: {
		id: 'snippets.panel.item.delete',
		defaultMessage: 'Delete',
		description: 'Delete block template menu item',
	},
});

type SnippetPanelActionsProps = {
	snippet: Snippet;
	isHovered: boolean;
	onMenuOpenChange?: (isOpen: boolean) => void;
};

export const SnippetPanelActions = ({
	snippet,
	isHovered,
	onMenuOpenChange,
}: SnippetPanelActionsProps) => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const intl = useIntl();
	const { handleEdit, handleDuplicate, handleDelete } = useSnippetActionHandlers();

	const handleMenuOpenChange = useCallback(
		({ isOpen }: { isOpen: boolean }) => {
			setIsMenuOpen(isOpen);
			onMenuOpenChange?.(isOpen);
		},
		[onMenuOpenChange],
	);

	return (
		<Box
			testId={`snippet-panel-actions-${snippet.snippetId}`}
			xcss={cx(
				styles.actionsContainer,
				(isHovered || isMenuOpen) && styles.actionsContainerVisible,
			)}
		>
			<AkTooltip
				content={isMenuOpen ? '' : intl.formatMessage(messages.moreActions)}
				position="bottom"
				hideTooltipOnClick
			>
				<AkDropDownMenu
					placement="bottom-end"
					testId={`snippet-ellipsis-menu-${snippet.snippetId}`}
					trigger={({ triggerRef, ...props }) => (
						<IconButton
							{...props}
							label={intl.formatMessage(messages.moreActions)}
							icon={MoreIcon}
							ref={triggerRef}
							appearance="subtle"
							aria-expanded={isMenuOpen ? 'true' : 'false'}
						/>
					)}
					appearance="tall"
					isOpen={isMenuOpen}
					onOpenChange={handleMenuOpenChange}
				>
					<DropdownItemGroup>
						<DropdownItem
							onClick={handleEdit}
							elemBefore={<EditIcon label={intl.formatMessage(messages.edit)} />}
						>
							<FormattedMessage {...messages.edit} />
						</DropdownItem>
						<DropdownItem
							onClick={handleDuplicate}
							elemBefore={<CopyIcon label={intl.formatMessage(messages.duplicate)} />}
						>
							<FormattedMessage {...messages.duplicate} />
						</DropdownItem>
						<DropdownItem
							onClick={handleDelete}
							elemBefore={<DeleteIcon label={intl.formatMessage(messages.delete)} />}
						>
							<FormattedMessage {...messages.delete} />
						</DropdownItem>
					</DropdownItemGroup>
				</AkDropDownMenu>
			</AkTooltip>
		</Box>
	);
};
